import React, { useState } from 'react';
import { ChefHat, Flame, Sparkles, Clock, UtensilsCrossed, Trophy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const Home = () => {
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const recipeSteps = [
    { icon: <UtensilsCrossed className="w-6 h-6" />, title: "The Foundation", desc: "Replace nori with a tortilla. Smash tater tots flat for 'rice'." },
    { icon: <Flame className="w-6 h-6" />, title: "The Core", desc: "Raw hotdog as 'tuna'. Add mozzarella and crushed French onions." },
    { icon: <ChefHat className="w-6 h-6" />, title: "The Wrap", desc: "Roll it like sushi. Sear the seam in a frying pan to seal." },
    { icon: <Clock className="w-6 h-6" />, title: "The Chill", desc: "Freeze for 15 minutes. Dust with cornstarch." },
    { icon: <Flame className="w-6 h-6" />, title: "The Fry", desc: "Dip in tempura batter. Deep fry to golden perfection." },
    { icon: <Sparkles className="w-6 h-6" />, title: "The Finale", desc: "Top with spicy mayo, jalapeños, fried onions. Slice like sushi." }
  ];

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    setEmailSubmitted(true);
    setTimeout(() => setEmailSubmitted(false), 3000);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-black" />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Crimson Text, serif' }}>PK Sushi Dogs</h1>
          </div>
          <Button className="bg-black text-white hover:bg-gray-800 transition-all duration-200 hover:-translate-y-0.5">
            Get Updates
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-full mb-6 text-sm font-semibold">
              <Sparkles className="w-4 h-4" />
              WORLD PREMIERE
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Crimson Text, serif' }}>
              The PK Sushi Dog
            </h1>
            <p className="text-2xl md:text-3xl text-gray-700 mb-4 max-w-3xl mx-auto" style={{ fontFamily: 'Crimson Text, serif' }}>
              When Late-Night Genius Meets Culinary Chaos
            </p>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A groundbreaking fusion that combines the elegance of sushi with the audacity of a deep-fried hot dog.
            </p>
          </div>

          {/* Main Image */}
          <div className="relative max-w-5xl mx-auto mb-8">
            <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_piksushipup/artifacts/f9nde3xv_ae92acab-efae-40e3-8fde-7af10ec60461.png" 
                alt="PK Sushi Dogs" 
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 bg-black text-white px-6 py-3 rounded-lg shadow-xl transform rotate-3">
              <p className="font-bold text-lg">FOR THE FIRST TIME EVER</p>
            </div>
          </div>
        </div>
      </section>

      {/* Origin Story */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-12 shadow-xl border-2 border-gray-200 hover:shadow-2xl transition-shadow duration-300">
            <div className="text-center mb-8">
              <Flame className="w-12 h-12 mx-auto mb-4 text-black" />
              <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
                The Origin Story
              </h2>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Late one night, fueled by decaf coffee and great conversation, I found myself in an unlikely culinary collaboration with ChatGPT. What started as a joke quickly evolved into something magnificent.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                "What if," we pondered, "we could make sushi... but with hot dogs?" And not just any hot dog sushi—we're talking tortilla nori, tater tot rice, and deep-fried tempura perfection.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed font-semibold">
                The PK Sushi Dog was born. And tonight, it becomes reality.
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* Recipe Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
              The Sacred Recipe
            </h2>
            <p className="text-xl text-gray-600">Six steps to culinary enlightenment</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {recipeSteps.map((step, index) => (
              <Card 
                key={index}
                className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-200"
              >
                <div className="bg-black text-white w-12 h-12 rounded-full flex items-center justify-center mb-4">
                  {step.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Crimson Text, serif' }}>
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Ingredients Highlight */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <Card className="p-12 shadow-xl border-2 border-black">
            <h2 className="text-4xl font-bold text-center mb-12" style={{ fontFamily: 'Crimson Text, serif' }}>
              The Legendary Ingredients
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">Nori Reinvented:</p>
                  <p className="text-gray-600">Premium flour tortilla</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">Rice Redefined:</p>
                  <p className="text-gray-600">Hand-smashed tater tots</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">The Protein:</p>
                  <p className="text-gray-600">Raw hot dog (the 'tuna')</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">The Binder:</p>
                  <p className="text-gray-600">Melty mozzarella cheese</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">The Crunch:</p>
                  <p className="text-gray-600">Crushed French fried onions</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-2 h-2 bg-black rounded-full mt-2"></div>
                <div>
                  <p className="font-bold text-lg">The Finish:</p>
                  <p className="text-gray-600">Spicy mayo, jalapeños, more onions</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <Sparkles className="w-16 h-16 mx-auto mb-6 text-black" />
            <h2 className="text-5xl font-bold mb-6" style={{ fontFamily: 'Crimson Text, serif' }}>
              The Results Are Coming
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              History is being made tonight. The PK Sushi Dog will be created, documented, and shared with the world.
            </p>
          </div>

          {!emailSubmitted ? (
            <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  placeholder="Enter your email for updates"
                  className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                  required
                />
                <Button 
                  type="submit"
                  className="bg-black text-white hover:bg-gray-800 px-8 py-4 transition-all duration-200 hover:-translate-y-0.5"
                >
                  Notify Me
                </Button>
              </div>
            </form>
          ) : (
            <div className="bg-black text-white px-6 py-4 rounded-lg inline-block">
              <p className="font-semibold">🎉 You're on the list! Prepare for glory.</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <Trophy className="w-12 h-12 mx-auto mb-4" />
            <p className="text-2xl font-bold mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>PK Sushi Dogs</p>
            <p className="text-gray-400">Where innovation meets intoxication</p>
          </div>
          <div className="border-t border-gray-700 pt-6">
            <p className="text-gray-500 text-sm">
              A collaboration between questionable life choices and AI-powered recipe generation.
            </p>
            <p className="text-gray-600 text-xs mt-2">
              © 2025 PK Sushi Dogs. Created under the influence of good times.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;